import { type FC, useState } from "react";
import { Link } from "~/common/components/Link";
import { Trans, useTranslation } from "react-i18next";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";
import { userIsRegistered } from "~/domains/user/utils/roles";
import {
  isDatasheetPage,
  redirectToUrl,
  userOnMyAccountPage,
  userOnRegistrationConfirmatioAccountPage,
  userOnRegistrationConfirmationEmailPage,
} from "~/utils/url/urlUtils";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useUndoPartnerPopupConfirmation } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/UndoPartnerPopupConfirmation";
import {
  displayAffiliatePartnerPopupBanner,
  saveAffiliatePartnerBannerClosedCookie,
  showAffiliatePartnerSaveBtn,
  useShowAffiliateMainBanner,
} from "~/domains/affiliate/utils/utils";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  trackBannerClose,
  trackBannerOnLoad,
  trackBannerRequestOfferClick,
  trackRegisterInAffiliate,
  trackBannerSave,
  trackBannerUndo,
} from "~/app/analytics/ga/affiliate";
import { AffiliateRequestOfferBannerView } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateRequestOfferBanner";
import { AffiliateRegisterBannerView } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateRegisterBanner";
import { AffiliateSavePartnerTileView } from "~/features/affiliate/components/AffiliateSalesPartnerBanner/components/AffiliateSavePartnerTile";
import { useIsMinDesktopSize, useIsMinPhoneSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Box, Button, Stack, Typography, styled, IconButton, type SxProps, type Theme } from "~/common/mui";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { saveVisitedSalesPartnerThunk, undoSaveAffiliateThunk } from "~/domains/affiliate/redux/actions";
import { Alert } from "~/common/mui/Alert/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { useAffiliateExpirationTimeHours } from "~/app/config/useAppConfig";

export interface AffiliateSalesPartnerBannerViewModel {
  partnerName: string;
  partnerLogo?: string;
  isRegistered: boolean;
  isSaved: boolean;
  showSaveButton: boolean;
  showRequestOffer: boolean;
  showMiniRequestOffer: boolean;
  showMiniBanner: boolean;
  isDesktop: boolean;
  isPhone: boolean;
  isTablet: boolean;
  copy: {
    welcome: string;
    requestOfferButton: string;
    saveText: string;
    savedText: string;
    saveButton: string;
    undoButton: string;
    changeAccount: string;
    registerText: string;
    registerButton: string;
    requestOfferText: string;
    loginMessageKey: string;
  };
}

export interface AffiliateSalesPartnerBannerViewProps {
  className?: string;
  sx?: SxProps<Theme>;
  viewModel: AffiliateSalesPartnerBannerViewModel;
  onClose: () => void;
  onSave: () => void;
  onUndo: () => void;
  onRequestOffer: () => void;
  onRegister: () => void;
  onLogin: () => void;
}

const BannerGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplate: '"title close" auto "affiliate affiliate" auto "wishlist wishlist" auto "undo undo" auto / 80% 20%',
  rowGap: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3.75),
    gridTemplate: '"title close" auto "affiliate wishlist" auto "undo undo" auto / 1fr 1fr',
    rowGap: 0,
    columnGap: theme.spacing(3.75),
  },
}));

const BannerTitle = styled(Typography)(({ theme }) => ({
  gridArea: "title",
  margin: 0,
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(4),
  },
}));

const CloseButton = styled(IconButton)(() => ({
  gridArea: "close",
  justifySelf: "flex-end",
  alignSelf: "flex-start",
}));

const MobileContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  borderRadius: theme.spacing(0.5),
  margin: theme.spacing(2),
}));

const PartnerLogo = styled("img")(() => ({
  width: 87,
  height: "auto",
}));

const MiniBannerLayout = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  margin: theme.spacing(2),
  marginTop: theme.spacing(-2),
  [theme.breakpoints.up("desktop-s")]: {
    margin: theme.spacing(4),
    marginTop: theme.spacing(-4),
  },
}));

export const AffiliateSalesPartnerBannerView = ({
  className,
  sx,
  viewModel,
  onClose,
  onSave,
  onUndo,
  onRequestOffer,
  onRegister,
  onLogin,
}: AffiliateSalesPartnerBannerViewProps) => {
  const {
    partnerName,
    partnerLogo,
    isRegistered,
    isSaved,
    showSaveButton,
    showRequestOffer,
    showMiniRequestOffer,
    showMiniBanner,
    isDesktop,
    isPhone,
    isTablet,
    copy,
  } = viewModel;
  const dispatch = useDispatch();

  const miniBanner = (
    <MiniBannerLayout sx={isPhone ? { flexDirection: "row", gap: 0 } : undefined}>
      <Stack direction="row" alignItems="center" gap={3}>
        {isPhone && partnerLogo && <PartnerLogo src={partnerLogo} />}
        <Typography variant={isDesktop ? "headlineSmall" : "labelLarge"} sx={{ margin: 0, mb: !isSaved ? 4 : 0 }}>
          {copy.welcome} <strong>{partnerName}</strong>
        </Typography>
      </Stack>
      {showMiniRequestOffer && (
        <Button
          sx={(theme) => ({ minHeight: 30, padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}` })}
          onClick={onRequestOffer}
          data-test-id={"banner-partner-request-offer"}
        >
          {copy.requestOfferButton}
        </Button>
      )}
    </MiniBannerLayout>
  );

  if (showMiniBanner) {
    return (
      <Box className={className} sx={sx}>
        {miniBanner}
      </Box>
    );
  }

  const content = (
    <BannerGrid>
      <BannerTitle variant={isDesktop ? "headlineSmall" : "labelLarge"}>
        {copy.welcome} <strong>{partnerName}</strong>
      </BannerTitle>

      <CloseButton onClick={onClose} color={"primary"}>
        <CloseIcon />
      </CloseButton>

      <Box sx={{ gridArea: "affiliate" }}>
        <AffiliateSavePartnerTileView
          onSaveClick={onSave}
          onUndoClick={onUndo}
          logo={partnerLogo}
          isSaved={!showSaveButton}
          isPhone={isPhone}
          isTablet={isTablet}
          saveText={copy.saveText}
          savedText={copy.savedText}
          saveButtonLabel={copy.saveButton}
          undoButtonLabel={copy.undoButton}
        />
      </Box>

      {isRegistered && showRequestOffer && (
        <Box sx={{ gridArea: "wishlist" }}>
          <AffiliateRequestOfferBannerView
            text={copy.requestOfferText}
            buttonLabel={copy.requestOfferButton}
            isTablet={isTablet}
            onRequestOffer={onRequestOffer}
          />
        </Box>
      )}

      {isRegistered && !showSaveButton && (
        <Typography sx={(theme) => ({ gridArea: "undo", [theme.breakpoints.up("desktop-s")]: { mt: 2.5 } })}>
          {copy.changeAccount}
        </Typography>
      )}

      {!isRegistered && (
        <Box sx={{ gridArea: "wishlist" }}>
          <AffiliateRegisterBannerView
            text={copy.registerText}
            buttonLabel={copy.registerButton}
            isPhone={isPhone}
            onRegister={onRegister}
          />
        </Box>
      )}

      {!isRegistered && !showSaveButton && (
        <Alert
          severity={"info"}
          sx={(theme) => ({ gridArea: "undo", [theme.breakpoints.up("desktop-s")]: { mt: 2.5 } })}
        >
          <Trans i18nKey={copy.loginMessageKey}>
            Please
            <Link
              className={"link-sign-in"}
              onClick={() => {
                dispatch(openAuthOverlay("register"));
              }}
            >
              register
            </Link>
            or
            <Link onClick={onLogin}>sign in</Link>
            to save permanently
          </Trans>
        </Alert>
      )}
    </BannerGrid>
  );

  return (
    <Box className={className} sx={sx}>
      {isDesktop ? <Box className="container mb-30">{content}</Box> : <MobileContainer>{content}</MobileContainer>}
    </Box>
  );
};

const AffiliateSalesPartnerBanner: FC = () => {
  const { t } = useTranslation();
  const expirationTimeHours = useAffiliateExpirationTimeHours();
  const shouldShowBanner = useShowAffiliateMainBanner();
  const [showBanner, setShowBanner] = useState<boolean>(shouldShowBanner);
  const isRegistered = userIsRegistered();
  const visitedAffiliate = useSelector(selectVisitedAffiliate);
  const savedAffiliate = useSelector(selectSavedAffiliate);
  const isSaved = !!visitedAffiliate && savedAffiliate?.affiliateId === visitedAffiliate?.affiliateId;

  useOnMount(() => {
    if (!isSaved) {
      trackBannerOnLoad(visitedAffiliate);
    }
  });

  const [showSaveButton, setShowSaveButton] = useState<boolean>(
    !!showAffiliatePartnerSaveBtn(savedAffiliate, visitedAffiliate),
  );

  const dispatch = useDispatch();

  const saveSalesPartner = async () => {
    const success = await dispatch(saveVisitedSalesPartnerThunk({ expirationTimeHours }));
    if (success) {
      setShowSaveButton(false);
    }
    trackBannerSave(visitedAffiliate);
  };

  const undoSalesPartner = async () => {
    const success = await dispatch(undoSaveAffiliateThunk({ expirationTimeHours }));
    if (success) {
      setShowSaveButton(true);
      trackBannerUndo(visitedAffiliate);
      close();
    }
  };

  const {
    open: openUndoModal,
    modal: undoModal,
    close,
  } = useUndoPartnerPopupConfirmation({
    onOk: undoSalesPartner,
    onCancel: () => close(),
  });

  const isDesktop = useIsMinDesktopSize();
  const isMobile = useIsMinPhoneSize();
  const isTablet = useIsMinTabletSize();

  if (!showBanner || !visitedAffiliate) {
    return null;
  }

  const closeBanner = () => {
    setShowBanner(false);
    trackBannerClose(visitedAffiliate);
    saveAffiliatePartnerBannerClosedCookie(visitedAffiliate.affiliateId, expirationTimeHours);
  };

  const isCheckout = window.location.pathname.includes(CART_PAGE);

  const onRequestOffer = () => {
    trackBannerRequestOfferClick(visitedAffiliate);
    redirectToUrl(CART_PAGE);
  };

  const onRegister = () => {
    trackRegisterInAffiliate(visitedAffiliate?.name ?? "");
    dispatch(openAuthOverlay("register"));
  };

  const viewModel: AffiliateSalesPartnerBannerViewModel = {
    partnerName: visitedAffiliate?.name ?? "",
    partnerLogo: visitedAffiliate?.logo,
    isRegistered,
    isSaved,
    showSaveButton,
    showRequestOffer: !isSaved && !isCheckout,
    showMiniRequestOffer: !isCheckout,
    showMiniBanner: isSaved && isRegistered,
    isDesktop,
    isPhone: isMobile,
    isTablet,
    copy: {
      welcome: t("affiliateSalesPartner.banner.text.welcome"),
      requestOfferButton: t("affiliateSalesPartner.banner.button.requestOffer"),
      saveText: t("affiliateSalesPartner.banner.text.save"),
      savedText: t("affiliateSalesPartner.banner.text.saved"),
      saveButton: t("affiliateSalesPartner.banner.button.save"),
      undoButton: t("common.undo"),
      changeAccount: t("affiliateSalesPartner.banner.message.changeAccount"),
      registerText: userHasPermission(Permission.ADD_TO_CART)
        ? t("affiliateSalesPartner.banner.new.text.register")
        : t("affiliateSalesPartner.banner.text.register"),
      registerButton: t("affiliateSalesPartner.banner.button.registerForFree"),
      requestOfferText: userHasPermission(Permission.ADD_TO_CART)
        ? t("affiliateSalesPartner.banner.new.text.wishlist")
        : t("affiliateSalesPartner.banner.text.wishlist"),
      loginMessageKey: "affiliateSalesPartner.banner.message.login",
    },
  };
  if (!showBanner) {
    return null;
  }
  if (!visitedAffiliate) {
    return null;
  }
  return (
    <>
      <AffiliateSalesPartnerBannerView
        viewModel={viewModel}
        onClose={closeBanner}
        onSave={saveSalesPartner}
        onUndo={openUndoModal}
        onRequestOffer={onRequestOffer}
        onRegister={onRegister}
        onLogin={() => dispatch(openAuthOverlay("login"))}
      />
      {undoModal}
    </>
  );
};

export function createAffiliateSalesPartnerBanner() {
  if (!displayAffiliatePartnerPopupBanner()) {
    return null;
  }
  if (
    userOnRegistrationConfirmationEmailPage() ||
    userOnRegistrationConfirmatioAccountPage() ||
    userOnMyAccountPage() ||
    isDatasheetPage()
  ) {
    return null;
  }

  return <AffiliateSalesPartnerBanner />;
}
